// src/lib/duckdb/tokenize.ts
import type { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";

export type SQLTokenType =
  | "whitespace"
  | "comment"
  | "string"
  | "number"
  | "keyword"
  | "identifier"
  | "punctuation";

export interface SQLToken {
  type: SQLTokenType;
  value: string;
  start: number;
  end: number;
}

// Regex captures: 
// 1: whitespace
// 2: line comment start (--)
// then block comments, strings, numbers, identifiers, punctuation
const TOKEN_REGEX = /(\s+)|(--)|\/\*[\s\S]*?\*\/|"(?:[^"]|"")*"|'(?:[^']|'')*'|`[^`]*`|\b\d+(\.\d+)?\b|\b[A-Za-z_][A-Za-z0-9_]*\b|[(),.;=*<>!+-]/g;

// A small set of SQL keywords for classification
const KEYWORDS = new Set([
  "SELECT","FROM","WHERE","JOIN","ON","GROUP","BY","HAVING","ORDER","LIMIT",
  "INSERT","INTO","VALUES","UPDATE","SET","DELETE","CREATE","TABLE","VIEW",
  "AS","AND","OR","NOT","IN","IS","NULL","PRIMARY","KEY","IF","EXISTS"
]);

/**
 * Split a SQL string into a flat array of tokens.
 * You can optionally use `conn` to run PRAGMA table_info
 * for identifiers followed by “.” to discover real column names
 * and inject them as extra tokens if you like.
 */
export async function tokenizeSQL(
  sql: string,
  conn?: AsyncDuckDBConnection
): Promise<SQLToken[]> {
  const tokens: SQLToken[] = [];
  let match: RegExpExecArray | null;

  while ((match = TOKEN_REGEX.exec(sql))) {
    const [value] = match;
    const index = match.index;
    let type: SQLTokenType;

    if (match[1]) {
      type = "whitespace";
    } else if (value.startsWith("--") || value.startsWith("/*")) {
      type = "comment";
    } else if (/^["'`]/.test(value)) {
      type = "string";
    } else if (/^\d/.test(value)) {
      type = "number";
    } else if (KEYWORDS.has(value.toUpperCase())) {
      type = "keyword";
    } else if (/^[(),.;=*<>!+-]$/.test(value)) {
      type = "punctuation";
    } else {
      type = "identifier";
    }

    tokens.push({ type, value, start: index, end: index + value.length });
  }

  // — optional PRAGMA example (not wired up):
  // for (let i = 0; i + 1 < tokens.length; i++) {
  //   if (tokens[i].type === "identifier" && tokens[i+1].value === ".") {
  //     const table = tokens[i].value;
  //     const colsRes = await conn?.query(`PRAGMA table_info('${table}')`);
  //     // parse colsRes and inject extra tokens...
  //   }
  // }

  return tokens;
}
